import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-1 whitespace-nowrap shrink-0",
    "rounded-md border px-2 py-0.5 text-[11px] font-medium leading-none w-fit",
    "[&>svg]:size-3 [&>svg]:pointer-events-none",
    "transition-colors duration-[var(--dur-fast)]",
  ),
  {
    variants: {
      variant: {
        default:
          "bg-bg-subtle border-border-subtle text-fg",
        outline:
          "bg-transparent border-border text-fg-muted",
        secondary:
          "bg-bg-subtle border-transparent text-fg-muted",
        primary:
          "bg-primary border-primary-border/40 text-fg",
        success:
          "bg-success-soft border-success-soft-fg/15 text-success-soft-fg",
        warning:
          "bg-warning-soft border-warning-soft-fg/15 text-warning-soft-fg",
        danger:
          "bg-danger-soft border-danger-soft-fg/15 text-danger-soft-fg",
        destructive:
          "bg-danger text-danger-fg border-transparent",
        info:
          "bg-info-soft border-info-soft-fg/15 text-info-soft-fg",
        ai:
          "bg-ai-soft border-ai-border/30 text-ai-soft-fg",
        solid:
          "bg-fg border-transparent text-bg",
      },
      size: {
        default: "px-2 py-0.5 text-[11px]",
        sm:      "px-1.5 py-0 text-[10px]",
        lg:      "px-2.5 py-1 text-xs",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
  /** Render a leading colored dot. */
  dot?: boolean;
}

function Badge({ className, variant, size, asChild = false, dot, children, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : "span";
  // Radix Slot uses React.Children.only — pass children directly when asChild,
  // since dot+children would be 2+ siblings.
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {dot ? <span className="size-1.5 rounded-full bg-current opacity-90" aria-hidden="true" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Badge, badgeVariants };
